import type { ColumnDef } from '@tanstack/react-table';
import styled from 'styled-components';

import { DataTable, dateColumn, textColumn } from 'components/DataTable';
import { Loader, LoaderModeType } from 'components/Loader';
import { TypeTag } from 'pages/Stocks/components/StockVariationTypeTag';
import { DetailLink } from 'components/DetailsView';
import { ClipboardValue } from 'components/ClipboardValue';
import { ColumnProductFragment, StockVariationType } from 'data/__generated__';
import { getModifierLink } from 'helpers/links/utils';
import { Theme } from 'contexts/theme';

type VariationRow = {
    _id: string;
    createdAt: number;
    delta: number | null;
    ref?: string | null;
    type: StockVariationType | null;
    explanation: string;
    stockBefore: number | null;
    stockAfter: number | null;
};

const columns: ColumnDef<VariationRow, unknown>[] = [
    textColumn<VariationRow, Record<string, unknown>>({
        accessorKey: '_id',
        header: 'Id',
        defaultVisible: true,
        cell: ({ row }) => <ClipboardValue value={row.original._id} isDataCentered />,
    }),
    dateColumn<VariationRow, Record<string, unknown>>({
        accessorKey: 'createdAt',
        header: 'Date de création',
        defaultVisible: true,
    }),
    textColumn<VariationRow, Record<string, unknown>>({
        id: 'delta',
        accessorFn: (row) => row.delta?.toString() ?? '-',
        header: 'Delta',
        defaultVisible: true,
        cell: ({ row }) => {
            const delta = row.original.delta;
            return delta ? <Delta $isPositive={delta > 0}>{delta}</Delta> : <NoDelta>-</NoDelta>;
        },
    }),
    textColumn<VariationRow, Record<string, unknown>>({
        id: 'stock',
        accessorFn: (row) => row.stockAfter?.toString() ?? '-',
        header: 'Stock',
        defaultVisible: true,
        cell: ({ row }) => {
            const { stockBefore, stockAfter } = row.original;
            if (stockAfter === null) {
                return <NoDelta>-</NoDelta>;
            }
            if (stockBefore === null) {
                return <Stock>{stockAfter}</Stock>;
            }
            return (
                <Stock>
                    {stockBefore} → {stockAfter}
                </Stock>
            );
        },
    }),
    textColumn<VariationRow, Record<string, unknown>>({
        accessorKey: 'ref',
        header: 'Référence',
        defaultVisible: true,
        cell: ({ row }) => {
            const ref = row.original.ref;
            const refInfos = ref ? getModifierLink(ref) : null;
            if (refInfos) {
                return <DetailLink path={refInfos.pathRoot} name={ref || 'Inconnu'} value={refInfos._id} />;
            }
            return ref || '';
        },
    }),
    textColumn<VariationRow, Record<string, unknown>>({
        accessorKey: 'type',
        header: 'Type',
        defaultVisible: true,
        cell: ({ row }) => <TypeTag type={row.original.type} />,
    }),
    textColumn<VariationRow, Record<string, unknown>>({
        accessorKey: 'explanation',
        header: 'Explication',
        defaultVisible: true,
    }),
];

export const StockVariationsList = ({
    stockVariations,
    inventories,
}: {
    stockVariations: ColumnProductFragment['variations'];
    inventories: ColumnProductFragment['inventories'];
}) => {
    if (!stockVariations) {
        return <Loader mode={LoaderModeType.Spin} />;
    }

    // Reconstruct the running stock at each variation. A variation is attached to the inventory it
    // derives from (columnInventoryStockId); the stock starts at that inventory's counted value and
    // evolves by applying the inventory's variations in chronological order.
    const inventoryValueById = new Map(inventories.map((inventory) => [inventory._id, inventory.value]));
    const variationsByInventoryId = new Map<string, ColumnProductFragment['variations']>();
    for (const variation of stockVariations) {
        const list = variationsByInventoryId.get(variation.columnInventoryStockId) ?? [];
        list.push(variation);
        variationsByInventoryId.set(variation.columnInventoryStockId, list);
    }

    const stockByVariationId = new Map<string, { before: number; after: number }>();
    for (const [inventoryId, inventoryVariations] of variationsByInventoryId) {
        const baseStock = inventoryValueById.get(inventoryId);
        if (baseStock === undefined) {
            continue;
        }
        const chronologicalVariations = [...inventoryVariations].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() || a._id.localeCompare(b._id),
        );
        let runningStock = baseStock;
        for (const variation of chronologicalVariations) {
            const before = runningStock;
            const after = runningStock + (variation.delta ?? 0);
            stockByVariationId.set(variation._id, { before, after });
            runningStock = after;
        }
    }

    const rows: VariationRow[] = [
        ...stockVariations.map((variation) => ({
            _id: variation._id,
            delta: variation.delta,
            ref: variation.ref,
            createdAt: variation.createdAt,
            explanation: variation.explanation || '',
            type: variation.type,
            stockBefore: stockByVariationId.get(variation._id)?.before ?? null,
            stockAfter: stockByVariationId.get(variation._id)?.after ?? null,
        })),
        ...inventories.map((inventory) => ({
            _id: inventory._id,
            delta: null,
            ref: inventory.modifierId,
            createdAt: inventory.createdAt,
            explanation: 'Inventaire',
            type: null,
            stockBefore: null,
            stockAfter: inventory.value,
        })),
    ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return (
        <DataTable<VariationRow>
            columns={columns}
            data={rows}
            filterMode="client"
            getRowId={(row) => row._id}
            autoFocusSearch={false}
        />
    );
};

const Delta = styled.div<{ $isPositive: boolean }>`
    color: ${({ theme, $isPositive }: { theme: Theme; $isPositive: boolean }) =>
        $isPositive ? theme.successColor : theme.errorColor};
`;

const NoDelta = styled.div``;

const Stock = styled.div`
    white-space: nowrap;
`;
